import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { RequiredLabel } from './RequiredLabel';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { FormInput } from './InputInterface';
import { useTranslation } from '@/hooks/useTranslation';

type DropDownListProps<T extends string> = FormInput<T> & {
  options: { label: string; value: T; id?: string | number }[];
  placeholder?: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
};

export const DropDownList = forwardRef(function DropDownList<T extends string>(
  { name, label, value, onChange, options, placeholder, required, side }: DropDownListProps<T>,
  ref: React.Ref<{ validate: () => boolean }>
) {
  const { t } = useTranslation();
  const htmlDivInputElement = useRef<HTMLDivElement>(null);
  const htmlInputElement = useRef<HTMLInputElement>(null);
  const [currentValue, setCurrentValue] = useState<T | null>(null);

  useEffect(() => {
    if (value) {
      setCurrentValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // expose validate() to parent
  useImperativeHandle(ref, () => ({
    validate: () => {
      if ((currentValue && currentValue != '0' && currentValue != '') || !required) {
        return true;
      }

      // Radix renders a hidden native <select> next to the trigger: it carries the validity bubble.
      htmlInputElement.current = htmlDivInputElement.current?.querySelector(
        'select'
      ) as unknown as HTMLInputElement | null;
      if (!htmlInputElement.current) return false;

      htmlInputElement.current.required = required;
      htmlInputElement.current.value = currentValue as string;

      const valid = htmlInputElement.current?.checkValidity();
      htmlInputElement.current?.reportValidity();
      if (valid) {
        return valid;
      }
      return false;
    },
    inputRef: htmlInputElement,
  }));

  function handleChange(value: T) {
    onChange(value);
    setCurrentValue(value);
  }

  return (
    <div className="space-y-2 w-full" ref={htmlDivInputElement}>
      <RequiredLabel htmlFor={name} label={label} required={required} />

      <Select value={value ?? undefined} onValueChange={handleChange}>
        <SelectTrigger id={name} className="w-full">
          <SelectValue placeholder={placeholder || t('form.select.options')} />
        </SelectTrigger>
        <SelectContent side={side} avoidCollisions={!side}>
          {options.map((opt) => (
            <SelectItem key={opt.id ?? opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
});
